import type { Paginated, PaginationQuery } from "@/configs/request";
import type {
  LedgerAccount,
  LedgerEntry,
  Payment,
  PaymentStatus,
  PlatformSettings,
  SettingRule,
  SortOrder,
  UserFinance,
  Withdrawal,
  WithdrawalStatus,
} from "@/types";

import { BaseApi } from "./base.api";

/** Serverda saralanadigan ustunlar — backenddagi ro'yxatlar bilan bir xil. */
export type LedgerSortField = "createdAt" | "amountCoffees";
export type PaymentSortField = "createdAt" | "paidAt" | "coffees" | "amountUzs";
export type WithdrawalSortField = "createdAt" | "processedAt" | "coffees" | "amountUzs";

/** Uchala ro'yxatda takrorlanadigan qism. */
interface BillingListFilter extends PaginationQuery {
  /** Aniq foydalanuvchi (kartochkadan kelganda). */
  userId?: string;
  /** Ism yoki Telegram username bo'yicha qidiruv. */
  user?: string;
  /** Yozuv vaqti oralig'i, ISO 8601. */
  createdFrom?: string;
  createdTo?: string;
  sortOrder?: SortOrder;
}

export interface LedgerFilter extends BillingListFilter {
  account?: LedgerAccount;
  amountCoffees?: number;
  refType?: string;
  refId?: string;
  note?: string;
  sortBy?: LedgerSortField;
}

export interface PaymentFilter extends BillingListFilter {
  status?: PaymentStatus;
  provider?: "payme" | "click";
  coffees?: number;
  amountUzs?: number;
  providerTxnId?: string;
  /** To'langan vaqt oralig'i. */
  paidFrom?: string;
  paidTo?: string;
  sortBy?: PaymentSortField;
}

export interface WithdrawalFilter extends BillingListFilter {
  status?: WithdrawalStatus;
  coffees?: number;
  amountUzs?: number;
  destination?: string;
  note?: string;
  /** Hal qilingan vaqt oralig'i. */
  processedFrom?: string;
  processedTo?: string;
  sortBy?: WithdrawalSortField;
}

class AdminBillingApi extends BaseApi {
  ledger(filter: LedgerFilter = {}) {
    return this.request<Paginated<LedgerEntry>>("/admin/ledger", { query: { ...filter } });
  }

  /**
   * Ikki yozuvli hisobning asosiy invarianti: barcha yozuvlar yig'indisi
   * nolga teng (docs/MONEY.md). Nolga teng bo'lmasa — bu tizimli xato,
   * va uni birinchi bo'lib admin ko'rishi kerak.
   */
  ledgerBalanceCheck() {
    return this.request<{ sumCoffees: number; balanced: boolean }>("/admin/ledger/balance-check");
  }

  payments(filter: PaymentFilter = {}) {
    return this.request<Paginated<Payment>>("/admin/payments", { query: { ...filter } });
  }

  withdrawals(filter: WithdrawalFilter = {}) {
    return this.request<Paginated<Withdrawal>>("/admin/withdrawals", { query: { ...filter } });
  }

  /**
   * Bitta foydalanuvchining butun moliyaviy kartochkasi.
   *
   * Bitta so'rovda: balans, yig'indi va oxirgi yozuvlar. Uchta alohida
   * so'rov sahifani uch marta "yuklanmoqda" holatidan o'tkazardi va
   * raqamlar bir-biriga mos kelmagan lahza paydo bo'lardi.
   *
   * Ruxsat `ledger.read`: profilni ko'rish huquqi pulni ko'rish
   * huquqini anglatmaydi.
   */
  userFinance(userId: string) {
    return this.request<UserFinance>(`/admin/users/${userId}/finance`);
  }

  settings() {
    return this.request<PlatformSettings>("/admin/settings");
  }

  /** Maydon chegaralari — forma validatsiyasi shu javobga tayanadi. */
  settingRules() {
    return this.request<SettingRule[]>("/admin/settings/rules");
  }

  updateSettings(data: Partial<PlatformSettings>) {
    return this.request<PlatformSettings>("/admin/settings", { method: "PUT", body: data });
  }

  /**
   * Chiqarishni to'langan deb belgilash (ADR 0019).
   *
   * Bank o'tkazmasi tizimdan tashqarida bajariladi — bu yerda uning
   * natijasi yoziladi. `note` da hujjat raqami turadi.
   */
  markWithdrawalPaid(id: string, note?: string) {
    return this.request<Withdrawal>(`/admin/withdrawals/${id}/mark-paid`, {
      method: "POST",
      body: { note },
    });
  }

  /**
   * Hisobni qo'lda tuzatish (docs/MONEY.md §5).
   *
   * YOZUV O'CHIRILMAYDI, USTIGA YOZILADI. Xato yozuvni tahrirlash
   * mumkin bo'lsa, «an'anaviy o'sha paytda nima bo'lgan edi?» degan
   * savolga javob yo'qoladi — va aynan shu savol har bir nizoda
   * beriladi.
   *
   * Javob — YANGILANGAN kartochka: admin tuzatish nimaga olib
   * kelganini darhol ko'radi va sahifani qayta yuklashi shart emas.
   */
  adjustLedger(input: { userId: string; coffees: number; note: string; account?: LedgerAccount }) {
    return this.request<UserFinance>("/admin/ledger/adjust", { method: "POST", body: input });
  }

  /** Rad etish — qahva mezbonning balansiga qaytadi. */
  rejectWithdrawal(id: string, reason: string) {
    return this.request<Withdrawal>(`/admin/withdrawals/${id}/reject`, {
      method: "POST",
      body: { reason },
    });
  }
}

export const adminBillingApi = new AdminBillingApi();
